import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Switch } from '@/components/ui/Switch';
import { useDashboardIdentity } from '@/hooks/useDashboardIdentity';
import { salesGet, salesPost } from '@/lib/salesTeam';
import { SalesCard } from './SalesPrimitives';
import styles from './SalesTeam.module.css';

type Row = { key: string; label: string; bell: boolean; email: boolean; email_default: boolean };
const SAVE_PATH = 'admin/sales-flow/notification-preferences';

// An in-app and an email switch per activity (Warren, 21 October 2026). The server decides which activities a person
// has and their defaults; this only shows the person's own choices. Each switch saves as it moves, sending the same full
// list the old Save button sent, one save at a time in order, so nothing about what is saved changes.
export function SalesNotificationPreferences({ title = 'Notifications' }: { title?: string }) {
  const identity = useDashboardIdentity(), [rows, setRows] = useState<Row[]>([]);
  const [saved, setSaved] = useState<string | null>(null), [error, setError] = useState('');
  const latest = useRef<Row[]>([]), queue = useRef<Promise<void>>(Promise.resolve()), pending = useRef(0);
  const q = useQuery({ queryKey: ['sales-notification-preferences', identity], queryFn: () => salesGet<{ events: Row[] }>(SAVE_PATH), refetchOnWindowFocus: false });
  const show = (next: Row[]) => { latest.current = next; setRows(next); };
  useEffect(() => { if (q.data && pending.current === 0) show(q.data.events); }, [q.data]);
  useEffect(() => { if (!saved) return; const t = setTimeout(() => setSaved(null), 2500); return () => clearTimeout(t); }, [saved]);
  const set = (key: string, change: Partial<Row>) => {
    const next = latest.current.map(r => r.key === key ? { ...r, ...change } : r);
    show(next); setError(''); pending.current += 1;
    queue.current = queue.current.then(async () => {
      try { await salesPost(SAVE_PATH, { events: next.map(({ key: k, bell, email }) => ({ key: k, bell, email })) }); setSaved(key); }
      catch {
        setError('Could not save that change. The switches show what is saved.');
        const fresh = await q.refetch().catch(() => null);
        if (fresh?.data) show(fresh.data.events);
      } finally { pending.current -= 1; }
    });
  };
  return <SalesCard title={title}>
    <p>Choose how each activity reaches you. Every change saves as you switch it.</p>
    {q.error && <p role="alert">Could not load notification preferences.</p>}
    {rows.length > 0 && <table className={styles.prefs}>
      <thead><tr><th scope="col">Activity</th><th scope="col">In app</th><th scope="col">Email</th></tr></thead>
      <tbody>{rows.map(r => <tr key={r.key}>
        <th scope="row">{r.label}{saved === r.key && <span className={styles.prefSaved} aria-hidden>Saved</span>}</th>
        <td><Switch hideLabel label={`${r.label}: in app`} checked={r.bell} onChange={e => set(r.key, { bell: e.target.checked })} /></td>
        <td><Switch hideLabel label={`${r.label}: email`} checked={r.email} onChange={e => set(r.key, { email: e.target.checked })} /></td>
      </tr>)}</tbody>
    </table>}
    <p role="status" className={error ? styles.prefError : styles.srOnly}>{error || (saved ? 'Saved' : '')}</p>
  </SalesCard>;
}
